// ABOUTME: Modal for selecting 1-2 datasources when creating a new investigation.
// ABOUTME: Shown before investigation creation, with checkbox multi-select and max-2 enforcement.

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Database, FileSpreadsheet, ShoppingBag, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { DataSource } from '@/types/api';

interface DatasourcePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (dataSourceIds: string[]) => void;
  dataSources: DataSource[];
  maxSources?: number;
}

export function DatasourcePickerModal({
  isOpen,
  onClose,
  onConfirm,
  dataSources,
  maxSources = 2,
}: DatasourcePickerModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Pre-select sources by creation date, respecting host compatibility.
  // Start with the oldest source, then only add compatible ones.
  const defaultSelected = useMemo(() => {
    const sorted = [...dataSources].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    const ids = new Set<string>();
    let groupHost: string | undefined;
    for (const ds of sorted) {
      if (ids.size >= maxSources) break;
      if (ids.size === 0) {
        ids.add(ds.id);
        groupHost = ds.host_group;
      } else if (!groupHost || !ds.host_group || ds.host_group === groupHost) {
        ids.add(ds.id);
      }
    }
    return ids;
  }, [dataSources, maxSources]);

  const [selected, setSelected] = useState<Set<string>>(defaultSelected);

  // Reset selection when modal opens or datasources change
  useEffect(() => {
    if (isOpen) {
      setSelected(defaultSelected);
    }
  }, [isOpen, defaultSelected]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  // Derive the host_group constraint from selected datasources
  const selectedHostGroup = useMemo(() => {
    if (selected.size === 0) return null;
    for (const ds of dataSources) {
      if (selected.has(ds.id) && ds.host_group) {
        return ds.host_group;
      }
    }
    return null;
  }, [selected, dataSources]);

  // Get the name of the first selected datasource for tooltip messaging
  const firstSelectedName = useMemo(() => {
    for (const ds of dataSources) {
      if (selected.has(ds.id)) return ds.name;
    }
    return null;
  }, [selected, dataSources]);

  const isHostIncompatible = useCallback((ds: DataSource): boolean => {
    if (!selectedHostGroup || !ds.host_group) return false;
    if (selected.has(ds.id)) return false;
    return ds.host_group !== selectedHostGroup;
  }, [selectedHostGroup, selected]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < maxSources) {
        next.add(id);
      }
      return next;
    });
  };

  const isAtMax = selected.size >= maxSources;

  const handleConfirm = () => {
    if (selected.size > 0) {
      onConfirm([...selected]);
    }
  };

  const getTypeIcon = (ds: DataSource) => {
    const displayType = ds.display_type || ds.type;
    if (displayType === 'shopify') return <ShoppingBag className="w-5 h-5 text-primary-600" />;
    if (displayType === 'csv') return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    return <Database className="w-5 h-5 text-blue-600" />;
  };

  const getTypeBadge = (ds: DataSource) => {
    const displayType = ds.display_type || ds.type;
    if (displayType === 'shopify') return 'Shopify';
    if (displayType === 'csv') return 'CSV';
    if (displayType === 'databricks') return 'Databricks';
    if (displayType === 'delta_sharing') return 'Delta Share';
    return displayType;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Dialog */}
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Select data sources"
            tabIndex={-1}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg bg-surface-0 border border-surface-200 rounded-sm shadow-xl outline-none"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200">
              <div>
                <h2 className="text-lg font-semibold text-surface-900">
                  Select data sources
                </h2>
                <p className="text-sm text-surface-500 mt-0.5">
                  Choose up to {maxSources} data source{maxSources > 1 ? 's' : ''} for this investigation
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-sm hover:bg-surface-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-surface-500" />
              </button>
            </div>

            {/* Datasource list */}
            <div className="px-6 py-4 max-h-80 overflow-y-auto">
              {dataSources.length === 0 ? (
                <p className="text-center text-surface-500 py-8">
                  No data sources configured. Connect a data source first.
                </p>
              ) : (
                <div className="space-y-2">
                  {dataSources.map((ds) => {
                    const isChecked = selected.has(ds.id);
                    const hostIncompat = isHostIncompatible(ds);
                    const isDisabled = (isAtMax && !isChecked) || hostIncompat;
                    const disabledTitle = hostIncompat && firstSelectedName
                      ? `Cannot combine with ${firstSelectedName} -- different Databricks connections`
                      : undefined;

                    return (
                      <button
                        key={ds.id}
                        onClick={() => !isDisabled && toggle(ds.id)}
                        disabled={isDisabled}
                        title={disabledTitle}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm border transition-colors text-left ${
                          isChecked
                            ? 'border-primary-500 bg-primary-50'
                            : isDisabled
                              ? 'border-surface-200 bg-surface-50 opacity-40 cursor-not-allowed'
                              : 'border-surface-200 hover:border-surface-300 hover:bg-surface-50 cursor-pointer'
                        }`}
                      >
                        {/* Checkbox */}
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isChecked
                              ? 'border-primary-500 bg-primary-500'
                              : 'border-surface-300'
                          }`}
                        >
                          {isChecked && <Check className="w-3 h-3 text-white" />}
                        </div>

                        {/* Icon */}
                        {getTypeIcon(ds)}

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-surface-900 truncate">
                            {ds.name}
                          </div>
                          <div className="text-xs text-surface-500">
                            {ds.csv_files
                              ? `${ds.csv_files.length} table${ds.csv_files.length !== 1 ? 's' : ''}`
                              : ds.config?.catalog || 'Connected'}
                          </div>
                        </div>

                        {/* Type badge */}
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            (ds.display_type || ds.type) === 'shopify'
                              ? 'bg-primary-100 text-primary-700'
                              : (ds.display_type || ds.type) === 'csv'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {getTypeBadge(ds)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-surface-200">
              <span className="text-sm text-surface-500">
                {selected.size} of {maxSources} selected
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={selected.size === 0}
                >
                  Start investigation
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
